import React from "react";
import { Navigation20Regular } from "@fluentui/react-icons";
import { MOBILE_NAV_VAR, contentHiddenBehindBar } from "../windowManager/bottomBars";
import { fmtBadge } from "./badgeFormat";

// Farbwelt der Bottom-Leiste (Courté-Plum + Gold) — bewusst als lokale
// Konstanten, damit die Leiste unabhängig vom NavigationLayout-internen NAV-Objekt
// bleibt. Werte entsprechen 1:1 der Desktop-Sidebar (NAV.bgGradient/accent).
const BAR = {
  bgGradient: "linear-gradient(180deg, #3b2240 0%, #28182d 100%)",
  text: "#ece3ea",
  activeText: "#ffffff",
  activeBg: "rgba(255,167,20,0.14)",
  accent: "#ffa714",
  font: "'Hanken Grotesk', 'Segoe UI', Tahoma, sans-serif",
} as const;

/** Ein Leisten-Eintrag — kompatibel mit den NavLeaf-Objekten im NavigationLayout. */
export interface MobileNavItem {
  value: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  /** Handlungsbedarf-Zähler — als Mini-Zähler-Pille (A7 revidiert: Zahl, „99+"-gekappt). */
  badge?: number;
}

export interface MobileBottomNavProps {
  /** Sichtbare Menüpunkte (bereits nach Rechten/„Zahlungsverkehr" gefiltert). */
  items: MobileNavItem[];
  /** Aktiver Menüpunkt (value) für die Hervorhebung. */
  activeValue: string;
  /** Öffnet das Vollbild-Menü („Menü"-Punkt rechts). */
  onOpenMenu: () => void;
  /** Ist das Vollbild-Menü offen? → „Menü"-Punkt gehighlightet. */
  menuOpen: boolean;
}

const ITEM_WIDTH = 66; // Touch-Target-Breite je scrollbarem Eintrag.

/**
 * MobileBottomNav — native-App-artige, FIXE Bottom-Navigation (nur Phone ≤768,
 * gerendert vom NavigationLayout). Plum-Leiste, horizontal scrollbare Items
 * (Icon + Label, Zähler-Pillen), rechts ein FIXER „Menü"-Punkt (Kreis-Highlight),
 * der das Vollbild-Menü öffnet. Immer sichtbar (kein Auto-Hide, User-Entscheidung).
 *
 * Layout-Vertrag (A6): Die tatsächlich gerenderte Höhe (inkl.
 * safe-area-inset-bottom) wird per ResizeObserver in die geteilte CSS-Variable
 * `--mobile-nav-h` gespiegelt; beim Unmount wird sie ENTFERNT (Fallback 0px).
 * `<main>`-Padding, Fenster-Dock, Sticky-Leisten, CallWidget und Kalender-FAB
 * staffeln sich darüber, sodass sich nichts mit der Leiste überschneidet.
 */
const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  items,
  activeValue,
  onOpenMenu,
  menuOpen,
}) => {
  const rootRef = React.useRef<HTMLElement | null>(null);

  // Gerenderte Höhe live an `--mobile-nav-h` (document root) spiegeln. Höhe ist
  // variabel (safe-area, Viewport-Resize) → ResizeObserver. Beim Unmount die
  // Variable ENTFERNEN (nicht "0px"!), damit die Konsumenten-Fallbacks greifen.
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    const el = rootRef.current;
    if (!el) return;
    const apply = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      root.style.setProperty(MOBILE_NAV_VAR, `${h}px`);
      // DEV-Runtime-Assertion (A6): nach dem Spiegeln prüfen, dass der Content
      // nicht hinter der fixen Leiste verschwindet (<main>-Padding ≥ Leistenhöhe).
      // Rein diagnostisch, nur im Dev-Build — im Prod-Build wegkompiliert.
      if (import.meta.env.DEV) {
        const main = document.querySelector("main");
        if (main) {
          const pad = parseFloat(getComputedStyle(main).paddingBottom) || 0;
          if (contentHiddenBehindBar(h, pad)) {
            console.warn(
              `[MobileBottomNav] <main>-Padding (${pad}px) < Leistenhöhe (${h}px) — Content verschwindet hinter der Leiste (A6).`,
            );
          }
        }
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty(MOBILE_NAV_VAR);
    };
  }, []);

  const renderItem = (it: MobileNavItem, active: boolean, fixed: boolean) => {
    const badgeLabel = fmtBadge(it.badge);
    const badgeWide = !!badgeLabel && badgeLabel.length >= 3; // „99+"
    return (
      <button
        key={it.value}
        type="button"
        aria-label={it.label}
        aria-current={active ? "page" : undefined}
        onClick={it.onClick}
        style={{
          flexShrink: 0,
          width: ITEM_WIDTH,
          minHeight: 56,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          padding: "6px 2px",
          border: "none",
          borderRadius: 12,
          cursor: "pointer",
          background: active ? BAR.activeBg : "transparent",
          color: active ? BAR.activeText : BAR.text,
          fontFamily: BAR.font,
          fontWeight: active ? 700 : 500,
          // Der fixe „Menü"-Slot rechts wird vom scrollbaren Rand abgesetzt.
          ...(fixed ? { marginLeft: 2 } : {}),
        }}
      >
        <span
          style={{
            position: "relative",
            display: "flex",
            width: 26,
            height: 26,
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            ...(active
              ? {
                  color: BAR.accent,
                  ...(fixed
                    ? {
                        // „Menü"-Punkt: Kreis-Highlight, wenn das Vollbild-Menü offen ist.
                        borderRadius: "50%",
                        boxShadow: `0 0 0 2px ${BAR.accent}`,
                      }
                    : {}),
                }
              : {}),
          }}
        >
          {it.icon}
          {/* A7 revidiert: Mini-Zähler-Pille (Gold auf Plum) statt Punkt, „99+"
              gekappt. Bei 3 Zeichen kleinere Schrift + mittig über der Ecke
              verankert, damit die breitere Pille auf schmalen Phones (iPhone SE,
              320px) im 66px-Item bleibt und nicht überlappt. */}
          {badgeLabel && (
            <span
              aria-hidden
              style={{
                position: "absolute",
                top: -6,
                right: badgeWide ? -4 : -6,
                transform: badgeWide ? "translateX(25%)" : undefined,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: BAR.accent,
                color: "#361836",
                fontSize: badgeWide ? 9 : 10,
                fontWeight: 700,
                lineHeight: 1,
                boxShadow: "0 0 0 2px #2c1730",
                boxSizing: "border-box",
              }}
            >
              {badgeLabel}
            </span>
          )}
        </span>
        <span
          style={{
            maxWidth: ITEM_WIDTH - 6,
            fontSize: 10.5,
            lineHeight: 1.1,
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {it.label}
        </span>
      </button>
    );
  };

  return (
    <nav
      ref={rootRef}
      aria-label="Hauptnavigation"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        // Unter dem CallWidget/ActiveCallBar-Portal (z 1000000), über dem
        // Seiteninhalt. Das Vollbild-Menü liegt mit höherem z-index darüber.
        zIndex: 1000,
        display: "flex",
        alignItems: "stretch",
        background: BAR.bgGradient,
        borderTop: "1px solid rgba(255,167,20,0.22)",
        boxShadow: "0 -6px 18px rgba(30,12,32,0.34)",
        // Mindesthöhe 84pt GESAMT (inkl. Safe-Area). Die max-Formel garantiert
        // die 84px-Untergrenze auch OHNE Notch (dort ist safe-area-inset-bottom
        // = 0) und wächst auf Home-Indicator-Geräten auf 64px Touch-Zone + Inset,
        // sobald das größer als 84px wird. box-sizing:border-box → das paddingBottom
        // (Inset) zählt in die Höhe hinein; darüber bleiben ≥64px tappbare Fläche.
        minHeight: "max(84px, calc(64px + env(safe-area-inset-bottom)))",
        // Home-Indicator-Bereich freihalten (greift nur mit viewport-fit=cover).
        paddingBottom: "env(safe-area-inset-bottom)",
        boxSizing: "border-box",
      }}
    >
      {/* Scrollbarer Bereich: alle Menüpunkte, horizontal scrollbar (A2). */}
      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <div
          className="mobile-bottom-nav-scroll"
          style={{
            display: "flex",
            alignItems: "stretch",
            gap: 2,
            padding: "4px 8px",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            // Scrollbar ausblenden (Firefox); WebKit über die CSS-Klasse unten.
            scrollbarWidth: "none",
          }}
        >
          {items.map((it) => renderItem(it, activeValue === it.value, false))}
        </div>
        {/* Fade-Kante rechts als Scroll-Affordance (A4). */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: 24,
            pointerEvents: "none",
            background: "linear-gradient(90deg, rgba(40,24,45,0) 0%, #2b1830 100%)",
          }}
        />
      </div>
      {/* Fixer „Menü"-Slot rechts — bleibt beim H-Scrollen stehen (A4/A5). */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          padding: "4px 8px 4px 4px",
          borderLeft: "1px solid rgba(255,167,20,0.18)",
          background: BAR.bgGradient,
        }}
      >
        {renderItem(
          { value: "__menu__", label: "Menü", icon: <Navigation20Regular />, onClick: onOpenMenu },
          menuOpen,
          true,
        )}
      </div>
      {/* WebKit-Scrollbar der Scroll-Zone ausblenden. */}
      <style>{`.mobile-bottom-nav-scroll::-webkit-scrollbar{display:none;height:0}`}</style>
    </nav>
  );
};

export default MobileBottomNav;
